import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import StatsCard from "@/components/dashboard/StatsCard";
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Users,
  UserPlus,
  UserMinus,
  TrendingDown,
  AlertCircle,
  Calendar,
  DollarSign,
  TrendingUp,
  Info,
} from "lucide-react";
import {
  fetchSubscriptionAnalytics,
  SubscriptionAnalytics,
  DateRange,
} from "@/api/business-analytics";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface SubscriptionTabProps {
  dateRange: DateRange;
}

// Custom tooltip component that supports dark mode
interface TooltipPayload {
  color: string;
  name: string;
  value: string | number;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const SubscriptionTab: React.FC<SubscriptionTabProps> = ({
  dateRange,
}) => {
  const {
    data: analytics,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      "subscriptionAnalytics",
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ],
    queryFn: () => fetchSubscriptionAnalytics(dateRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Hooks must be invoked unconditionally; derive memoized values with safe defaults
  const netGrowth = useMemo(
    () =>
      analytics
        ? analytics.newSubscriptions - analytics.churnedSubscriptions
        : 0,
    [analytics]
  );
  const growthRate = useMemo(
    () =>
      analytics && analytics.activeSubscriptions > 0
        ? (netGrowth / analytics.activeSubscriptions) * 100
        : 0,
    [analytics, netGrowth]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load subscription analytics. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No subscription data available for the selected date range.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subscription Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Active Subscriptions"
          value={analytics.activeSubscriptions}
          icon={<Users className="h-4 w-4" />}
          trend={{
            value: Number(Math.abs(growthRate).toFixed(2)),
            isPositive: growthRate >= 0,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="New Subscriptions"
          value={analytics.newSubscriptions}
          icon={<UserPlus className="h-4 w-4" />}
          trend={{
            value:
              analytics.newSubscriptions > analytics.churnedSubscriptions
                ? 100
                : 50,
            isPositive:
              analytics.newSubscriptions > analytics.churnedSubscriptions,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Churned Subscriptions"
          value={analytics.churnedSubscriptions}
          icon={<UserMinus className="h-4 w-4" />}
          trend={{
            value: analytics.churnRate,
            isPositive: false,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Churn Rate"
          value={`${analytics.churnRate}%`}
          icon={<TrendingDown className="h-4 w-4" />}
          trend={{
            value: analytics.churnRate,
            isPositive: analytics.churnRate <= 3,
            period: "prev. period",
          }}
        />
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Current MRR
            </CardTitle>
            <CardDescription>
              Monthly Recurring Revenue (normalized for comparison)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              ₹{analytics.currentMRR.toFixed(2)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <Info className="h-4 w-4 inline mr-1" />
              Annual plans converted to monthly equivalents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Actual Cash Flow
            </CardTitle>
            <CardDescription>
              Total money received in selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              ₹{analytics.actualCashFlow.toFixed(2)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Actual payments from {format(dateRange.from, "MMM d")} to{" "}
              {format(dateRange.to, "MMM d")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Revenue Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Actual Cash Flow Trends</CardTitle>
            <CardDescription>
              Real money received each month (what customers actually paid)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
            >
              <RechartsBarChart data={analytics.historicalTrends}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" className="text-muted-foreground" />
                <YAxis className="text-muted-foreground" />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="actualCashReceived"
                  fill="#3b82f6"
                  name="Actual Cash Received"
                  radius={[4, 4, 0, 0]}
                  className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                />
              </RechartsBarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Actual Cash Flow:</strong> Shows the real money received
                each month. Annual subscribers contribute larger amounts in
                their signup month.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>MRR Trends</CardTitle>
            <CardDescription>
              Monthly Recurring Revenue (normalized for comparison)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-line]:hover:brightness-110 [&_.recharts-line]:transition-all [&_.recharts-line]:duration-200"
            >
              <LineChart data={analytics.historicalTrends}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" className="text-muted-foreground" />
                <YAxis className="text-muted-foreground" />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="totalMRR"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Total MRR"
                  className="hover:brightness-110"
                />
                <Line
                  type="monotone"
                  dataKey="newMRR"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="New MRR"
                  className="hover:brightness-110"
                />
                <Line
                  type="monotone"
                  dataKey="churnedMRR"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Churned MRR"
                  className="hover:brightness-110"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>MRR:</strong> Monthly Recurring Revenue shows business
                health. Annual plans are divided by 12 for consistent
                comparison.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Count Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Growth Trends</CardTitle>
          <CardDescription>
            Monthly subscription acquisition and churn patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer
            width="100%"
            height={350}
            className="[&_.recharts-line]:hover:brightness-110 [&_.recharts-line]:transition-all [&_.recharts-line]:duration-200"
          >
            <LineChart data={analytics.historicalTrends}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="month" className="text-muted-foreground" />
              <YAxis className="text-muted-foreground" />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="totalSubscriptions"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Total Subscriptions"
                className="hover:brightness-110"
              />
              <Line
                type="monotone"
                dataKey="newSubscriptions"
                stroke="#10b981"
                strokeWidth={2}
                name="New Subscriptions"
                className="hover:brightness-110"
              />
              <Line
                type="monotone"
                dataKey="churnedSubscriptions"
                stroke="#ef4444"
                strokeWidth={2}
                name="Churned Subscriptions"
                className="hover:brightness-110"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Subscribers */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Subscribers</CardTitle>
          <CardDescription>
            Latest subscriptions in the selected date range
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.recentSubscribers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No new subscribers in the selected date range.
              </p>
            ) : (
              analytics.recentSubscribers.map((subscriber) => (
                <div
                  key={subscriber.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors duration-200"
                >
                  <div>
                    <p className="font-medium">{subscriber.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {subscriber.email}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary">{subscriber.plan}</Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      {format(
                        new Date(subscriber.subscriptionDate),
                        "MMM d, yyyy"
                      )}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
